/**
 * InverterDatabase - Inverter Equipment Database
 *
 * Full CRUD interface for managing inverter equipment in the database.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { PowerInput as InverterIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import { useAuth } from '@vortx/shared-ui';
import EquipmentTable from '../EquipmentTable';
import EquipmentForm from './EquipmentForm';
import { API_BASE_URL } from '../../../services/api';

const INVERTER_COLUMNS = [
    { id: 'manufacturer', label: 'Manufacturer', width: '18%' },
    { id: 'model', label: 'Model', width: '18%' },
    { id: 'ac_output_power_kw', label: 'AC Power (kW)', width: '12%', highlight: true, render: (v) => v?.toLocaleString() },
    { id: 'coupling_type', label: 'Coupling Type', width: '12%' },
    { id: 'equipment_cost', label: 'Equipment ($M)', width: '14%', highlight: true, render: (v) => v ? `$${(v/1000000).toFixed(2)}M` : '-' },
    { id: 'status', label: 'Status', width: '10%' }
];

function InverterDatabase() {
    const { currentUser } = useAuth();
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingEquipment, setEditingEquipment] = useState(null);

    const fetchEquipment = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/equipment/inverter?status=active`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch inverter equipment');
            const data = await res.json();

            // Sort alphabetically by manufacturer, then by model
            const sorted = (data.equipment || []).sort((a, b) => {
                const manufacturerCompare = (a.manufacturer || '').localeCompare(b.manufacturer || '');
                if (manufacturerCompare !== 0) return manufacturerCompare;
                return (a.model || '').localeCompare(b.model || '');
            });

            setEquipment(sorted);
        } catch (err) {
            console.error('[InverterDatabase] Error fetching equipment:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchEquipment();
    }, [fetchEquipment]);

    const handleCreate = () => {
        setEditingEquipment(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditingEquipment(item);
        setShowForm(true);
    };

    const handleCopy = (item) => {
        const { id, created_at, created_by, created_by_email, updated_at, version_history, ...copiedData } = item;
        setEditingEquipment(copiedData);
        setShowForm(true);
    };

    const handleSave = async (data) => {
        try {
            const token = await currentUser.getIdToken();
            const url = editingEquipment?.id
                ? `${API_BASE_URL}/api/site_dna/v1/equipment/inverter/${editingEquipment.id}`
                : `${API_BASE_URL}/api/site_dna/v1/equipment/inverter`;
            const method = editingEquipment?.id ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(data)
            });

            if (!res.ok) throw new Error('Failed to save equipment');

            setShowForm(false);
            setEditingEquipment(null);
            fetchEquipment();
        } catch (err) {
            console.error('[InverterDatabase] Error saving equipment:', err);
            alert(`Error: ${err.message}`);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Delete ${item.manufacturer} ${item.model}?`)) return;

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/equipment/inverter/${item.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Failed to delete equipment');
            fetchEquipment();
        } catch (err) {
            console.error('[InverterDatabase] Error deleting equipment:', err);
            alert(`Error: ${err.message}`);
        }
    };

    return (
        <Box>
            <Alert
                severity="info"
                icon={<InverterIcon />}
                sx={{
                    mb: 3,
                    bgcolor: alpha(vortxColors.gold, 0.1),
                    color: vortxColors.white,
                    '& .MuiAlert-icon': { color: vortxColors.gold }
                }}
            >
                Manage inverter specifications and pricing. All equipment must support 700V DC bus.
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <EquipmentTable
                title="Inverters"
                description="Manage inverter specifications and pricing."
                columns={INVERTER_COLUMNS}
                data={equipment}
                loading={loading}
                onAdd={handleCreate}
                onEdit={handleEdit}
                onCopy={handleCopy}
                onDelete={handleDelete}
                onRefresh={fetchEquipment}
                emptyMessage="No inverters in database. Click 'Add Entry' to create one."
            />

            {showForm && (
                <EquipmentForm
                    open={showForm}
                    type="inverter"
                    equipment={editingEquipment}
                    onSave={handleSave}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingEquipment(null);
                    }}
                />
            )}
        </Box>
    );
}

export default InverterDatabase;
